import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, Chip, ChipProps, IconButton, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';

import { useGetChainRateRules, useInvalidateChainRateRules } from '~/hooks/apis/reservation/useGetChainRateRules';

import ReservationService from '~/services/ReservationService';

import DeleteRuleDialog from './DeleteRuleDialog';
import RateRuleModal, { RateRule } from './RateRuleModal';

interface Props {
  chainId: string;
}

const matchTypeColor = (type: string): ChipProps['color'] => {
  switch (type) {
    case 'suffix':
      return 'info';
    case 'prefix':
      return 'error';
    case 'exact':
      return 'warning';
    case 'regex':
      return 'success';
    default:
      return 'default';
  }
};

export default function ChainRateRulesTab({ chainId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RateRule | null>(null);
  const [deleteRule, setDeleteRule] = useState<RateRule | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: rules = [], isLoading, isError, error } = useGetChainRateRules(chainId);
  const invalidateRules = useInvalidateChainRateRules();
  const rulesById = useMemo(() => new Map(rules.map((r) => [r.id, r])), [rules]);

  const handleCreate = () => {
    setEditingRule(null);
    setModalOpen(true);
  };

  const handleEdit = (rule: RateRule) => {
    setEditingRule(rule);
    setModalOpen(true);
  };

  const handleSubmit = async (data: Record<string, unknown>) => {
    setSaving(true);
    try {
      if (editingRule) {
        await ReservationService.updateChainRateRule(chainId, editingRule.id, data);
        enqueueSnackbar('Rate rule updated', { variant: 'success' });
      } else {
        await ReservationService.createChainRateRule(chainId, data);
        enqueueSnackbar('Rate rule created', { variant: 'success' });
      }
      setModalOpen(false);
      invalidateRules(chainId);
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to save rate rule', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRule) return;
    try {
      await ReservationService.deleteChainRateRule(chainId, deleteRule.id);
      enqueueSnackbar('Rate rule deleted', { variant: 'success' });
      setDeleteRule(null);
      invalidateRules(chainId);
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to delete rate rule', { variant: 'error' });
    }
  };

  const columns: Array<GridColDef<RateRule>> = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      display: 'flex',
      minWidth: 180,
    },
    {
      field: 'match_type',
      headerName: 'Match',
      width: 100,
      display: 'flex',
      renderCell: (params) => <Chip label={params.value} color={matchTypeColor(params.value)} size="small" />,
    },
    {
      field: 'match_value',
      headerName: 'Value',
      width: 140,
      display: 'flex',
      renderCell: (params) => <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{params.value}</span>,
    },
    {
      field: 'tier',
      headerName: 'Tier',
      width: 90,
      display: 'flex',
      renderCell: (params) =>
        params.value ? (
          <Chip label={params.value} size="small" variant="outlined" />
        ) : (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            global
          </Box>
        ),
    },
    {
      field: 'rate_category',
      headerName: 'Category',
      width: 100,
      display: 'flex',
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'package' ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'rate_type',
      headerName: 'Rate Type',
      width: 80,
      display: 'flex',
    },
    {
      field: 'markup',
      headerName: 'Markup / Commission %',
      width: 180,
      display: 'flex',
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_value, row) => {
        if (row.rate_type === 'net' && row.markup != null) return Number(row.markup);
        if (row.rate_type === 'gross' && row.commission != null) return Number(row.commission);
        return null;
      },
      renderCell: (params) => {
        if (params.value == null) return '—';
        const label = params.row.rate_type === 'gross' ? 'commission' : 'markup';
        return `${params.value}% ${label}`;
      },
    },
    {
      field: 'paired_rule_id',
      headerName: 'Paired Rule',
      flex: 1,
      minWidth: 140,
      display: 'flex',
      valueGetter: (_value, row) => {
        if (!row.paired_rule_id) return null;
        return rulesById.get(row.paired_rule_id)?.name ?? row.paired_rule_id;
      },
      renderCell: (params) =>
        params.value ? (
          <span style={{ fontFamily: 'monospace', fontSize: '0.8em' }}>{params.value}</span>
        ) : (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            —
          </Box>
        ),
    },
    {
      field: 'is_blocked',
      headerName: 'Status',
      width: 90,
      display: 'flex',
      renderCell: (params) => (
        <Chip label={params.value ? 'Blocked' : 'Active'} color={params.value ? 'error' : 'success'} size="small" />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteRule(params.row);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" onClick={handleCreate}>
          + Add Rule
        </Button>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Failed to load rate rules'}
        </Alert>
      )}

      <Box sx={{ width: '100%' }}>
        <DataGrid
          columns={columns}
          rows={rules}
          loading={isLoading}
          autoHeight
          pageSizeOptions={[25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          onRowClick={(params) => handleEdit(params.row)}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-columnHeaderTitle': { whiteSpace: 'normal', lineHeight: 1.2 },
            '& .MuiDataGrid-columnHeader': { alignItems: 'flex-end' },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          disableColumnFilter
          disableDensitySelector
        />
      </Box>

      <RateRuleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        existingRule={editingRule}
        allRules={rules}
        loading={saving}
      />

      <DeleteRuleDialog
        open={!!deleteRule}
        ruleName={deleteRule?.name || ''}
        matchInfo={
          deleteRule
            ? `${deleteRule.match_type}: ${deleteRule.match_value}${
                deleteRule.tier ? ` (tier: ${deleteRule.tier})` : ''
              }`
            : ''
        }
        onClose={() => setDeleteRule(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
